<template>
  <div>
    <ModalBlank :id="`${id}`" @close-modal="maybeDismiss">
      <div class="p-5 flex space-x-4 relative">
        <!-- Close -->
        <span class="absolute top-4 right-4">
          <AppButton
            v-if="dismissible"
            icon="pi pi-times"
            text
            variant="danger"
            class="ml-auto opacity-80"
            @click.stop="dismissible && emit('cancel')"
          />
        </span>
        <!-- Icon -->
        <div
          class="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          :class="{
            'bg-primary-100 dark:bg-primary-500/30': !type || type === 'info',
            'bg-rose-100 dark:bg-rose-500/30': type === 'danger',
            'bg-amber-100 dark:bg-amber-500/30': type === 'success',
          }"
        >
          <svg
            v-if="type === 'danger'"
            class="w-4 h-4 shrink-0 fill-current text-rose-500"
            viewBox="0 0 16 16"
          >
            <path
              d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z"
            />
          </svg>
          <svg
            v-else-if="type === 'success'"
            class="w-4 h-4 shrink-0 fill-current text-emerald-500"
            viewBox="0 0 16 16"
          >
            <path
              d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zM7 11.4L3.6 8 5 6.6l2 2 4-4L12.4 6 7 11.4z"
            />
          </svg>
          <svg
            v-else
            class="w-4 h-4 shrink-0 fill-current text-primary-500"
            viewBox="0 0 16 16"
          >
            <path
              d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 12H7V7h2v5zM8 6c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1z"
            />
          </svg>
        </div>
        <!-- Content -->
        <div class="w-full">
          <!-- Modal header -->
          <div class="mb-2">
            <div class="text-lg font-semibold text-slate-800 dark:text-slate-100">
              {{ title || $t("modal.title") }}
            </div>
          </div>
          <!-- Modal content -->
          <div class="text-sm mb-10">
            <div class="space-y-2">
              <p>
                {{ message || $t("modal.message") }}
              </p>
            </div>
          </div>
          <slot />
          <!-- Modal footer -->
          <div class="flex flex-wrap justify-end w-full space-x-2">
            <AppButton
              v-if="dismissible"
              :label="cancellationButtonText"
              variant="secondary"
              text
              @click.stop="
                () => {
                  emit('secondaryAction');
                  emit('cancel');
                }
              "
            />
            <AppButton
              :disabled="disabled || loading"
              :variant="type === 'danger' ? type : undefined"
              :loading="loading"
              :label="confirmationButtonText"
              class="px-6"
              @click="emit('confirm')"
            />
          </div>
        </div>
      </div>
    </ModalBlank>
  </div>
</template>

<script setup lang="ts">
import ModalBlank from "@/partials/ModalBlank.vue";
import type { DialogComponentProps } from "@/types/appConfirmDialog";
import AppButton from "./AppButton.vue";

const props = withDefaults(defineProps<DialogComponentProps>(), {
  type: "info",
  title: "Confirm",
  dismissible: true,
  transparentBackground: false,
});
const emit = defineEmits(["confirm", "cancel", "secondaryAction"]);

const { t } = withI18nHelpers();

const confirmationButtonText = computed(() =>
  props.primaryButtonText ? props.primaryButtonText : t("modal.primaryButtonText")
);
const cancellationButtonText = computed(() =>
  props.secondaryButtonText ? props.secondaryButtonText : t("modal.secondaryButtonText")
);

function maybeDismiss() {
  if (!props.dismissible) return;
  emit("cancel");
}
</script>

<style scoped></style>
